import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardFooter } from "@/components/ui/Card";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
const { VITE_VIEW_S3_URL } = import.meta.env;
import { default as defaultImg } from "@/assets/default.jpg";
import useTableData from "@/components/ui/table/DataTableLogic";
import usePagination from "@/hooks/UsePagination";
import { useGetTagsQuery } from "@/services/TagsApi";
import { Event } from "@/app/types/event";
import { DepartmentTagConfig, TagListItem } from "@/app/types/tags";
import { KioskLayoutContext } from "./layout";

const SkeletonCard: React.FC = () => (
  <div className="animate-pulse group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED]">
    <div className="w-full h-[150px] bg-gray-300 rounded-md"></div>
    <div className="mt-4 w-3/4 h-4 bg-gray-300 rounded-md"></div>
  </div>
);

const initialValue = {
  limit: 8,
  offset: 0
}

const { VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL, VITE_ORG_ID } = import.meta.env;

const KioskDepartmentPage: React.FC = () => {
  const itemsPerPage = 8; // Number of cards per page
  const navigate = useNavigate();
  const { searchCriteria, query, setSearchCriteria } = useTableData(initialValue);
  const location = useLocation();
  const event: Event = location.state?.event; // Retrieve passed data
  const { setTitle, setTitleClassname } = useOutletContext<KioskLayoutContext>();

  useEffect(() => {
    setTitle(event.name);
    setTitleClassname("text-primary");
  }, [setTitle]);

  const { data: tagsData, isLoading } = useGetTagsQuery({
    org_id: VITE_ORG_ID,
    tag_type: "department",
    event_ids: [event.event_id],
    limit: searchCriteria.limit,
    offset: searchCriteria.offset
  });

  const {
    data: tags = [], 
    total_count: totalRecords = 0
  } = tagsData ?? {};

  const {
    handleNextPage,
    handlePrevPage,
    isLastPage
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleCardClick = async (cardData: TagListItem) => {
    navigate(`/kiosk-service-offer/${cardData.tag_id}`, {
      state: { event, department: cardData },
    });
  };
  
  if (isLoading) {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <SkeletonCard key={Number(index)} />
    ));
  }

  if (!tags.length) (
    <div className="text-center py-6 flex justify-center items-center">
      <h2 className="text-gray-600 text-[1.2rem] font-semibold">
        No Department available
      </h2>
    </div>
  );

  return (
    <>
      <Button
        onClick={() => {
          navigate(-1);
        }}
        variant={"ghost"}
        className="w-16 mt-2"
      >
        {" "}
        <ChevronLeftIcon className="h-6 w-6" />
      </Button>
      {/* Services Section */}
      <section className="mx-4 grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-0 md:mt-8">
        {tags.map((item, index) => (
          <Card
            key={Number(index)}
            onClick={() => handleCardClick(item)}
            className="group relative flex flex-col items-center justify-center w-full py-4 bg-[#EDEDED] hover:cursor-pointer"
          >
            <CardContent className="flex flex-col items-center justify-center text-center leading-0">
              <div className="w-[100%] h-[100%]">
                <img
                  src={
                    (item.metadata as DepartmentTagConfig).logo
                      ? `${VITE_VIEW_S3_URL + (item.metadata as DepartmentTagConfig).logo}`
                      : defaultImg
                  }
                  alt="Company logo"
                  className="w-[150px] h-[150px] object-contain"
                />
              </div>
            </CardContent>

            <CardFooter className="flex items-center justify-center text-center">
              <p className="capitalize text-[#767676] m-0 group-hover:text-white p-0 font-semibold text-[1.2rem]">
                {item.name}
              </p>
            </CardFooter>
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="text-center px-2">
                <p className="capitalize text-[1.2rem] font-bold m-0">
                  {item.name}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* Pagination Controls */}
      <div className="flex items-center justify-end gap-4 my-4 w-full">
        {searchCriteria.offset !== 0 && <Button
          variant="solid"
          size="icon"
          onClick={handlePrevPage}
          disabled={searchCriteria.offset === 0}
          className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="h-7 w-7" />
        </Button>}

        <Button
          variant="solid"
          size="icon"
          onClick={handleNextPage}
          disabled={isLastPage}
          className="text-[2.1rem] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="h-7 w-7" />
        </Button>
      </div>
    </>
  );
};

export default KioskDepartmentPage;
